import styled from '@emotion/styled'
import React, { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { colors } from '@/styles/theme'
import { ColorSelector } from '@/components/atoms/ColorSelector'
import { SexSelector } from '@/components/atoms/SexSelector'
import { VoiceChatSelector } from '@/components/atoms/VoiceChatSelector'
import {
  InputText,
  InputSelector,
  OptionsPlaytime,
} from '@/components/atoms/Inputs'
import { VoiceChat } from '@/firebase/firestore/VoiceChats'
import { Platform } from '@/firebase/firestore/Platform'
import { Sex } from '@/firebase/firestore/Sex'
import { PlatformSelector } from '@/components/atoms/PlatformSelector'
import { useLocale } from '@/hooks/useLocale'
import { format } from 'date-fns'
import Image from 'next/image'
import { ProfileCardEditorDescription } from '@/components/atoms/ProfileCardEditorDescription'
import { CREATORS, creatorMapTargetId, weaponGroups } from './config'
import { MonsterHunterWilds1 } from './MonsterHunterWilds1'
import { MonsterHunterWildsSelector } from './MonsterHunterWildsSelector'

type FormField = {
  name: string
  playerId: string
  voiceChatDetail: string
  hitokoto: string
  playTimeWeekDayBegin: string
  playTimeWeekDayEnd: string
  playTimeWeekEndBegin: string
  playTimeWeekEndEnd: string
  iconImage: string
  favoriteMonster: string
  hunterRank: string
}

const FONT_COLORS = [
  '#FD8080',
  '#FD7C24',
  '#29DDF2',
  '#C05AE0',
  '#7AA852',
  '#4C4C4C',
]

const TEMPLATE_COLORS = [
  '#BBBBBB',
  '#F1D6C0',
  '#FAC2D2',
  '#D7C2B1',
  '#919191',
  '#F8E39D',
  '#FDCF9D',
  '#A9D7E2',
  '#9CBDE3',
  '#C0B3DC',
  '#89D5B3',
  '#A8E4B6',
  '#FDBA9B',
]

type Props = {
  onCreate: (
    data: FormField & { selectedWeapons: number[] },
    targetId: string,
    selectedSex: Sex,
    selectedPlatform: Array<Platform>,
    selectedVoiceChat: Array<VoiceChat>
  ) => void
  submitButtonDisable: boolean
}

export const EditorMonsterHunterWilds: React.FC<Props> = ({
  onCreate,
  submitButtonDisable,
}) => {
  const { t } = useLocale()
  const { handleSubmit, register, watch } = useForm<FormField>({
    defaultValues: {
      playTimeWeekDayBegin: '18',
      playTimeWeekDayEnd: '20',
      playTimeWeekEndBegin: '18',
      playTimeWeekEndEnd: '20',
    },
  })
  const fieldVal = watch()

  const [selectedFontColor, setFontColor] = useState(0)
  const [selectedColor, setColor] = useState<
    0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
  >(0)
  const [preview, setPreview] = useState<string | null>(null)

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }
  const onChangeFontColor = useCallback((selectedIndex: number) => {
    setFontColor(selectedIndex)
  }, [])

  const onChangeColor = useCallback((selectedIndex: number) => {
    setColor(
      selectedIndex as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
    )
  }, [])

  const [selectedSex, setSex] = useState<Sex>(0)
  const onChangeSex = useCallback((selectedIndex: Sex) => {
    setSex(selectedIndex)
  }, [])

  const [selectedPlatform, setPlatform] = useState<Array<Platform>>([0])
  const onChangePlatform = useCallback((selectedIndex: Array<Platform>) => {
    setPlatform(selectedIndex)
  }, [])

  const [selectedVoiceChat, setVoiceChat] = useState<Array<VoiceChat>>([])
  const onChangeVoiceChat = useCallback((selectedIndex: Array<VoiceChat>) => {
    setVoiceChat(selectedIndex)
  }, [])

  const [selectedWeapons, setWeapons] = useState<number[]>([])
  const handleWeaponChange = useCallback((indices: number[]) => {
    setWeapons(indices.sort((a, b) => a - b))
  }, [])

  const handleCreate = (data: FormField) => {
    onCreate(
      { ...data, selectedWeapons },
      creatorMapTargetId[CREATORS[0]],
      selectedSex,
      selectedPlatform,
      selectedVoiceChat
    )
  }

  const lastModText = format(new Date(), 'yyyy年MM月dd日')

  return (
    <>
      <Image
        width="1920"
        height="1080"
        priority={true}
        unoptimized={true}
        src={`/static/images/promotes/monsterHunterWilds.jpg`}
        alt={`モンスターハンターワイルズ自己紹介カード作成ツール`}
      />
      <DescriptionSection>
        <Time>
          <time dateTime={new Date().toISOString()} itemProp="datepublished" />
          <time dateTime={new Date().toISOString()} itemProp="modified">
            {t.最終更新}: {lastModText}
          </time>
        </Time>
        <H1Text>
          {t.モンスターハンターワイルズかわいい自己紹介カード作成ツール}
        </H1Text>
        <H2Text>
          {t.モンスターハンターワイルズかわいい自己紹介カード作成ツール説明文}
        </H2Text>
        <ProfileCardEditorDescription />
      </DescriptionSection>
      <PreviewContainer>
        <PreviewCardContainer>
          <MonsterHunterWilds1
            fontColor={FONT_COLORS[selectedFontColor]}
            targetId={creatorMapTargetId[CREATORS[0]]}
            name={fieldVal.name}
            previewImage={preview}
            playerId={fieldVal.playerId}
            voiceChatDetail={fieldVal.voiceChatDetail}
            platform={selectedPlatform}
            hitokoto={fieldVal.hitokoto}
            voiceChat={selectedVoiceChat}
            sex={selectedSex}
            weapons={selectedWeapons}
            playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
            playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
            playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
            playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
            templateColor={selectedColor}
            favoriteMonster={fieldVal.favoriteMonster}
            hunterRank={fieldVal.hunterRank}
          />
        </PreviewCardContainer>
      </PreviewContainer>

      <FormContainer>
        <form onSubmit={handleSubmit(handleCreate)}>
          <FormItemContainer>
            <SectionHeader>カードの背景色を選ぶ</SectionHeader>
            <ColorSelector
              colors={TEMPLATE_COLORS}
              value={selectedColor}
              onChange={onChangeColor}
            />
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>文字色を選ぶ</SectionHeader>
            <ColorSelector
              colors={FONT_COLORS}
              value={selectedFontColor}
              onChange={onChangeFontColor}
            />
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>{t.なまえ}</SectionHeader>
            <InputText {...register('name', { maxLength: 20 })} />
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>アイコン画像</SectionHeader>
            <input type="file" accept="image/*" onChange={handleImageUpload} />
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>ID</SectionHeader>
            <InputText {...register('playerId', { maxLength: 50 })} />
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>{t.性別}</SectionHeader>
            <SexSelector val={selectedSex} onChange={onChangeSex} />
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>プラットフォーム</SectionHeader>
            <PlatformSelector
              onChange={onChangePlatform}
              value={selectedPlatform}
              activeValues={[0, 1, 2]} // PC, PlayStation, Xbox
            />
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>{t.好きなモンスター}</SectionHeader>
            <InputText {...register('favoriteMonster', { maxLength: 50 })} />
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>{t.プレイ時間帯平日}</SectionHeader>
            <InputSelector {...register('playTimeWeekDayBegin')}>
              <OptionsPlaytime />
            </InputSelector>
            <TimeSelectorText>から</TimeSelectorText>
            <InputSelector {...register('playTimeWeekDayEnd')}>
              <OptionsPlaytime />
            </InputSelector>
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>{t.プレイ時間帯土日}</SectionHeader>
            <InputSelector {...register('playTimeWeekEndBegin')}>
              <OptionsPlaytime />
            </InputSelector>
            <TimeSelectorText>から</TimeSelectorText>
            <InputSelector {...register('playTimeWeekEndEnd')}>
              <OptionsPlaytime />
            </InputSelector>
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>{t.ハンターランク}</SectionHeader>
            <InputText {...register('hunterRank', { maxLength: 50 })} />
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>ボイスチャット</SectionHeader>
            <VoiceChatSelector
              onChange={onChangeVoiceChat}
              value={selectedVoiceChat}
              activeValue={[0, 1, 2, 3, 4]} // あり・なし
            />
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>使用武器</SectionHeader>
            <MonsterHunterWildsSelector
              values={weaponGroups.weapons.images}
              names={weaponGroups.weapons.names}
              onChange={handleWeaponChange}
            />
          </FormItemContainer>

          <FormItemContainer>
            <SectionHeader>{t.ひとこと}</SectionHeader>
            <InputText {...register('hitokoto', { maxLength: 50 })} />
          </FormItemContainer>

          <ButtonDescriptionText>
            {t.カードの作成にはSNS連携が必要です次の画面でログインをします}
          </ButtonDescriptionText>
          <Submit
            id="create_profile"
            type="submit"
            value={t.これでつくる}
            disabled={submitButtonDisable}
          />
        </form>
      </FormContainer>
    </>
  )
}

const PreviewContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 1000;
`

const PreviewCardContainer = styled.div`
  background-color: ${colors.bg.primary};
  box-shadow: 0 0 4px rgba(32, 32, 32, 0.1);
`

const FormContainer = styled.div`
  padding: 20px;
`

const FormItemContainer = styled.div`
  margin-bottom: 32px;
`

const SectionHeader = styled.div`
  margin-top: 20px;
  margin-bottom: 16px;
  font-weight: bold;
  font-size: 18px;
  color: ${colors.text.primary};
  display: flex;
`

const TimeSelectorText = styled.div`
  margin: 8px auto;
  color: ${colors.text.primary};
  font-size: 12px;
  font-weight: bold;
`

const ButtonDescriptionText = styled.h3`
  margin: auto;
  margin-top: 30px;
  width: 100%;
  font-size: 10px;
  color: ${colors.text.secondary};
  justify-content: center;
  display: flex;
`

const Submit = styled.input`
  margin-top: 12px;
  border-radius: 20px;
  border: 3px solid transparent;
  width: 100%;
  height: 40px;
  background-color: ${colors.button.primary};
  color: #fff;
  font-weight: bold;
  display: block;
`

const DescriptionSection = styled.div`
  margin-top: 16px;
  margin-left: 16px;
  margin-right: 16px;
  margin-bottom: 30px;
`

const Time = styled.div`
  color: ${colors.text.secondary};
  font-size: 13px;
`

const H1Text = styled.h1`
  margin-top: 12px;
  font-size: 18px;
  font-weight: bold;
  line-height: 20px;
  color: ${colors.text.primary};
  text-align: center;
  line-height: 1.6;
  display: flex;
`

const H2Text = styled.h2`
  margin-top: 20px;
  font-size: 13px;
  color: ${colors.text.secondary};
  line-height: 1.6;
  display: flex;
`
